import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as rosterActions from '../../_actions/roster.actions';
import moment from 'moment';
import PropTypes from 'prop-types';
import './RosterPreview.css';
const canUseDOM = typeof window !== 'undefined' && typeof document !== 'undefined';

class RosterPreview extends Component {
    constructor(props) {
        super(props);
        this.state = {
            startTime: '',
            endTime: '',
            notes: '',
            confirmDelete: false,
            deleting: false,
            deleteError: null,
            saving: false,
            saveError: null,
            timeError: null,
        };
    }

    componentDidMount() {
        this._syncFromProps();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.event !== this.props.event) {
            this._syncFromProps();
        }
    }

    _syncFromProps() {
        const { event } = this.props;
        if (!event) return;

        const finalStart = event.start || event.dateFrom || event.PayrollEmpRosterStartDate;
        const finalEnd   = event.end  || event.dateTo   || event.PayrollEmpRosterEndDate;

        this.setState({
            startTime:     event.startTime || (finalStart ? moment(finalStart).format('HH:mm') : ''),
            endTime:       event.endTime   || (finalEnd   ? moment(finalEnd).format('HH:mm')   : ''),
            notes:         event.Note || event.PayrollEmpRosterActionNotes || '',
            confirmDelete: false,
            saving:        false,
            saveError:     null,
            timeError:     null,
            deleting:      false,
            deleteError:   null,
        });
    }

    handleClose = () => {
        if (this.props.onClose) {
            this.props.onClose();
        }
    };

    // -- Time validation --------------------------------------------------------
    /**
     * Returns an error string if start >= end, null if valid.
     * Both values are "HH:mm" strings.
     */
    _validateTimes(start, end) {
        if (!start || !end) return null;
        const [sh, sm] = start.split(':').map(Number);
        const [eh, em] = end.split(':').map(Number);
        const startMins = sh * 60 + sm;
        const endMins   = eh * 60 + em;
        if (startMins >= endMins) {
            return `Start time (${start}) must be before end time (${end}).`;
        }
        return null;
    }

    handleStartTimeChange = (e) => {
        const startTime = e.target.value;
        const timeError = this._validateTimes(startTime, this.state.endTime);
        this.setState({ startTime, timeError, saveError: null });
    };

    handleEndTimeChange = (e) => {
        const endTime = e.target.value;
        const timeError = this._validateTimes(this.state.startTime, endTime);
        this.setState({ endTime, timeError, saveError: null });
    };

    handleSave = async () => {
        const { event, actions, onClose, onRefresh } = this.props;
        const { startTime, endTime, notes } = this.state;

        // Guard: block save if times are logically invalid
        const timeError = this._validateTimes(startTime, endTime);
        if (timeError) {
            this.setState({ timeError });
            return;
        }

        // Derive the roster date (date-only, time zeroed) from the original event
        const rosterDate = moment(
            event.start || event.dateFrom || event.PayrollEmpRosterStartDate
        ).startOf('day').toISOString();

        // Parse HH:mm strings into hour/minute integers
        const [startHour, startMinute] = startTime.split(':').map(Number);
        const [endHour,   endMinute]   = endTime.split(':').map(Number);

        // Build payload matching the PUT /api/etime/preview expected shape
        const payload = {
            RosterAutoID:           event.id || event.RosterAutoID,
            RosterCompanyID:        event.CompanyId || event.CompanyID || event.RosterCompanyID,
            RosterEmployeeID:       event.EmployeeID || event.employeeID || event.RosterEmployeeID,
            RosterDate:             rosterDate,
            RosterEndDate:          rosterDate,
            RosterStartTimeHour:    startHour,
            RosterStartTimeMinute:  startMinute,
            RosterFinishTimeHour:   endHour,
            RosterFinishTimeMinute: endMinute,
            RosterNotes:            notes,
            RosterApproved:         true,
            RosterWorkPlanID:       event.RosterWorkPlanID || 0,
            RosterOriginalRosterID: event.RosterOriginalRosterID || 0,
            RosterLocation1ID:      event.RosterLocation1ID || 0,
            RosterLocation2ID:      event.RosterLocation2ID || 0,
            RosterLocation3ID:      event.RosterLocation3ID || 0,
            RosterLocation4ID:      event.RosterLocation4ID || 0,
            RosterLocation5ID:      event.RosterLocation5ID || 0,
            RosterTaskID:           event.RosterTaskID || 0,
        };

        this.setState({ saving: true, saveError: null });

        const result = await actions.updatePreviewEvent(payload);

        if (result && result.success === false) {
            this.setState({ saving: false, saveError: result.error || 'Save failed. Please try again.' });
        } else {
            this.setState({ saving: false });
            if (onClose) onClose();
            if (onRefresh) onRefresh();
        }
    };

    handleDeleteClick = () => {
        this.setState({ confirmDelete: true });
    };

    handleDeleteCancel = () => {
        this.setState({ confirmDelete: false, deleteError: null });
    };

    handleDeleteConfirm = async () => {
        const { event, actions, onClose, onRefresh } = this.props;
        const rosterAutoID = event.id || event.RosterAutoID;

        this.setState({ deleting: true, deleteError: null });

        const result = await actions.deletePreviewEvent(rosterAutoID);

        if (result && result.success === false) {
            this.setState({ deleting: false, deleteError: result.error || 'Delete failed. Please try again.' });
        } else {
            this.setState({ deleting: false, confirmDelete: false });
            if (onClose) onClose();
            if (onRefresh) onRefresh();
        }
    };

    buildTitle(event) {
        if (event.start && event.end) {
            return `${moment(event.start).format('HH:mm')} - ${moment(event.end).format('HH:mm')}`;
        }
        return 'Shift';
    }

    render() {
        const { event, editMode } = this.props;
        if (!event) return null;

        const { startTime, endTime, notes, confirmDelete, saving, saveError, timeError, deleting, deleteError } = this.state;

        const finalStart = event.start || event.dateFrom || event.PayrollEmpRosterStartDate;

        const title = event.Title || event.title || event.PayrollEmpRosterDispTitle || this.buildTitle(event);
        const dateValue = finalStart ? moment(finalStart).format('DD/MM/YYYY') : '';
        const employeeDisplay = event.EmployeeName || '';

        const modalContent = (
            <div className="rp-overlay" onClick={this.handleClose}>
                <div className="rp-modal" onClick={e => e.stopPropagation()}>

                    <div className="rp-header">
                        <h2 className="rp-title">{title}</h2>
                        <button className="rp-close" onClick={this.handleClose}>&times;</button>
                    </div>

                    <div className="rp-body">
                        {/* Employee � always read-only */}
                        <div className="rp-field">
                            <label className="rp-label">Employee</label>
                            <input type="text" className="rp-input" value={employeeDisplay} readOnly />
                        </div>

                        {/* Start / Until */}
                        <div className="rp-row">
                            <div className="rp-field">
                                <label className="rp-label">Start</label>
                                {editMode ? (
                                    <input
                                        type="time"
                                        className={`rp-input rp-input--editable rp-time${timeError ? ' rp-input--error' : ''}`}
                                        value={startTime}
                                        onChange={this.handleStartTimeChange}
                                    />
                                ) : (
                                    <input type="text" className="rp-input" value={startTime} readOnly />
                                )}
                            </div>
                            <div className="rp-field">
                                <label className="rp-label">Until</label>
                                {editMode ? (
                                    <input
                                        type="time"
                                        className={`rp-input rp-input--editable rp-time${timeError ? ' rp-input--error' : ''}`}
                                        value={endTime}
                                        onChange={this.handleEndTimeChange}
                                    />
                                ) : (
                                    <input type="text" className="rp-input" value={endTime} readOnly />
                                )}
                            </div>
                        </div>

                        {/* Inline time validation error */}
                        {editMode && timeError && (
                            <div className="rp-time-error">
                                <i className="pi pi-exclamation-circle" /> {timeError}
                            </div>
                        )}

                        {/* Date � always read-only */}
                        <div className="rp-field">
                            <label className="rp-label">Date</label>
                            <input type="text" className="rp-input" value={dateValue} readOnly />
                        </div>

                        {/* Notes */}
                        <div className="rp-field">
                            <label className="rp-label">Notes</label>
                            <textarea
                                className={`rp-input rp-textarea${editMode ? ' rp-input--editable' : ''}`}
                                rows="3"
                                readOnly={!editMode}
                                value={notes}
                                onChange={editMode ? e => this.setState({ notes: e.target.value }) : undefined}
                            />
                        </div>

                        {/* Save error banner */}
                        {saveError && (
                            <div className="rp-save-error">
                                <i className="pi pi-exclamation-triangle" /> {saveError}
                            </div>
                        )}

                        {/* Delete confirmation inline banner */}
                        {confirmDelete && (
                            <div className="rp-confirm-delete">
                                <span>Delete this shift? This cannot be undone.</span>
                                {deleteError && (
                                    <div className="rp-save-error">
                                        <i className="pi pi-exclamation-triangle" /> {deleteError}
                                    </div>
                                )}
                                <div className="rp-confirm-actions">
                                    <button
                                        className="rp-btn-cancel-delete"
                                        onClick={this.handleDeleteCancel}
                                        disabled={deleting}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="rp-btn-confirm-delete"
                                        onClick={this.handleDeleteConfirm}
                                        disabled={deleting}
                                    >
                                        {deleting
                                            ? <><i className="pi pi-spin pi-spinner" /> Deleting�</>
                                            : 'Yes, delete'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="rp-footer">
                        {/* Delete sits on the left */}
                        {editMode && (
                            <button
                                className="rp-btn-delete"
                                onClick={this.handleDeleteClick}
                                disabled={confirmDelete || deleting}
                            >
                                <i className="pi pi-trash" /> Delete
                            </button>
                        )}

                        <div className="rp-footer-right">
                            <button className="rp-btn-close" onClick={this.handleClose}>Close</button>
                            {editMode && (
                                <button
                                    className="rp-btn-save"
                                    onClick={this.handleSave}
                                    disabled={confirmDelete || saving || !!timeError}
                                >
                                    {saving ? <><i className="pi pi-spin pi-spinner" /> Saving�</> : 'Save'}
                                </button>
                            )}
                        </div>
                    </div>

                </div>
            </div>
        );

        if (canUseDOM) {
            return ReactDOM.createPortal(modalContent, document.body);
        }
        return modalContent;
    }
}

RosterPreview.defaultProps = {
    editMode: false,
};

RosterPreview.propTypes = {
    event:    PropTypes.object,
    onClose:  PropTypes.func,
    editMode: PropTypes.bool,
    onRefresh: PropTypes.func,
};

function mapStateToProps(state) {
    return {
        displayEmployees: state.roster.displayEmployees,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        actions: bindActionCreators(rosterActions, dispatch),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(RosterPreview);
